import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { useSendMessageLogic } from './useSendMessageLogic';
import SlashCommandsPopover from './SlashCommandsPopover';
import ComposerHeader from './ComposerHeader';
import ComposerFilePreview from './ComposerFilePreview';
import ComposerFooter from './ComposerFooter';
import ManageShortcutsModal from './ManageShortcutsModal';
import TicketNotesModal from '../TicketNotesModal';
import ImageEditorModal from './ImageEditorModal';

const SendMessage = (props) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const logic = useSendMessageLogic(props);

  return (
    <div
      className="position-relative rounded-3 shadow-sm"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
        transition: 'border-color 0.15s ease, background-color 0.15s ease'
      }}
    >
      {/* 1. Popover de autocompletado para Slash Commands al escribir '/' */}
      <SlashCommandsPopover
        showSuggestions={logic.showSuggestions}
        filteredCommands={logic.filteredCommands}
        selectedIndex={logic.selectedIndex}
        setSelectedIndex={logic.setSelectedIndex}
        applyCommand={logic.applyCommand}
        suggestionsRef={logic.suggestionsRef}
        isDark={isDark}
      />

      {/* 2. Barra Superior: Atajos rápidos */}
      <ComposerHeader
        hasOpenTickets={logic.hasOpenTickets}
        mergePillTitle={logic.mergePillTitle}
        mergePillLabel={logic.mergePillLabel}
        mergePromptMessage={logic.mergePromptMessage}
        displayQuickCommands={logic.displayQuickCommands}
        onShortcutClick={logic.handleShortcutClick}
        onOpenShortcutsModal={() => logic.setIsShortcutsModalOpen(true)}
        isDark={isDark}
      />

      {/* 3. Previsualización de archivo adjunto (imagen, video, audio o documento) */}
      <ComposerFilePreview
        filePreview={props.filePreview}
        onRemoveFile={logic.handleRemoveFile}
        onEditImage={() => logic.setIsImageEditorOpen(true)}
        isDark={isDark}
      />

      {/* 3.1 Previsualización de mensaje al que se responde (Reply Bar estilo WhatsApp Web) */}
      {props.replyingTo && (
        <div
          className="mx-3 mt-2 p-2 rounded-2 d-flex align-items-center justify-content-between position-relative"
          style={{
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#f1f5f9',
            borderLeft: `4px solid ${
              props.replyingTo.bEsAgente || props.replyingTo.isAgent ? '#25d366' : '#38bdf8'
            }`,
            animation: 'fadeIn 0.15s ease'
          }}
        >
          <div className="overflow-hidden pe-2" style={{ maxWidth: '90%' }}>
            <div
              className="fw-bold text-truncate"
              style={{
                fontSize: '0.74rem',
                color: (props.replyingTo.bEsAgente || props.replyingTo.isAgent)
                  ? (isDark ? '#4ade80' : '#059669')
                  : (isDark ? '#38bdf8' : '#0284c7')
              }}
            >
              Respondiendo a {props.replyingTo.vCreatedUser || props.replyingTo.author || ((props.replyingTo.bEsAgente || props.replyingTo.isAgent) ? 'Asesor' : 'Cliente')}
            </div>
            <div
              className="text-truncate"
              style={{
                fontSize: '0.78rem',
                color: isDark ? '#94a3b8' : '#64748b',
                lineHeight: '1.3'
              }}
            >
              {props.replyingTo.idCatTipoMensaje === 2 ? '📷 Foto'
                : props.replyingTo.idCatTipoMensaje === 3 ? '💟 Sticker'
                : props.replyingTo.idCatTipoMensaje === 4 ? '🎤 Mensaje de voz'
                : props.replyingTo.idCatTipoMensaje === 5 ? '🎥 Video'
                : props.replyingTo.idCatTipoMensaje === 6 ? '📄 Documento'
                : (props.replyingTo.vTextoMensaje || props.replyingTo.text || 'Mensaje')}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm p-1 rounded-circle border-0 d-flex align-items-center justify-content-center"
            style={{
              width: '24px',
              height: '24px',
              color: isDark ? '#94a3b8' : '#64748b',
              backgroundColor: 'transparent'
            }}
            onClick={props.onCancelReply}
            title="Cancelar respuesta (Esc)"
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.80rem' }} />
          </button>
        </div>
      )}

      {/* 4. Área de Texto sin bordes internos */}
      <div className="px-3 pt-2 pb-1">
        <textarea
          ref={logic.textInputRef}
          rows={1}
          className="form-control border-0 p-0 shadow-none"
          style={{
            backgroundColor: 'transparent',
            color: isDark ? '#f9fafd' : '#0f172a',
            fontSize: '0.90rem',
            lineHeight: '1.45',
            resize: 'none',
            overflowY: 'auto',
            minHeight: '44px',
            maxHeight: '140px'
          }}
          placeholder={
            props.selectedFile
              ? 'Escribe un pie de foto o mensaje opcional...'
              : 'Escribe un mensaje o escribe "/" para atajos...'
          }
          value={props.inputText}
          onChange={e => {
            props.setInputText(e.target.value);
            if (e.target.value.trim().length > 0) {
              props.onTyping?.();
            } else {
              props.onStopTyping?.();
            }
          }}
          onKeyDown={logic.handleKeyDown}
          onPaste={logic.handlePaste}
          autoFocus
        />
      </div>

      {/* 5. Barra Inferior: Botón '+' con menú, Formato, Emojis, Shift+Enter y Enviar */}
      <ComposerFooter
        isDark={isDark}
        onApplyFormat={logic.applyFormatWrapper}
        showEmojiPicker={logic.showEmojiPicker}
        setShowEmojiPicker={logic.setShowEmojiPicker}
        emojiPickerRef={logic.emojiPickerRef}
        onInsertEmoji={logic.insertEmoji}
        showPlusMenu={logic.showPlusMenu}
        setShowPlusMenu={logic.setShowPlusMenu}
        plusMenuRef={logic.plusMenuRef}
        onSelectPlusAction={logic.handleSelectPlusAction}
        isSending={props.isSending}
        canSend={logic.canSend}
        onSubmit={logic.handleSubmit}
      />

      {/* Input de archivo oculto */}
      <input
        type="file"
        ref={logic.fileInputRef}
        hidden
        accept="image/*,video/*"
        onChange={logic.handleFileChange}
      />

      {/* Modal para administrar o crear atajos */}
      {logic.isShortcutsModalOpen && (
        <ManageShortcutsModal
          isOpen={logic.isShortcutsModalOpen}
          show={logic.isShortcutsModalOpen}
          onClose={() => {
            logic.setIsShortcutsModalOpen(false);
            props.onReloadAtajos?.();
          }}
          commands={props.commands}
          onReload={props.onReloadAtajos}
          onShortcutAdded={() => props.onReloadAtajos?.()}
          onSelectShortcut={cmd => logic.handleShortcutClick(cmd.vTexto || cmd.text || '')}
          onSelectCommand={logic.handleShortcutClick}
        />
      )}

      {/* Modal para crear Nota Interna si se selecciona desde el botón '+' */}
      {logic.showTicketNoteModal && (
        <TicketNotesModal
          show={logic.showTicketNoteModal}
          onClose={() => logic.setShowTicketNoteModal(false)}
          ticket={props.ticket || {}}
          createNote={props.onCreateTicketNote}
          updateNote={props.onUpdateTicketNote || (() => {})}
          isSavingNote={props.isSavingNote}
        />
      )}

      {/* Modal para editar imagen antes de enviar estilo WhatsApp Web */}
      {logic.isImageEditorOpen && props.filePreview?.type === 'image' && (
        <ImageEditorModal
          isOpen={logic.isImageEditorOpen}
          filePreview={props.filePreview}
          inputText={props.inputText}
          setInputText={props.setInputText}
          onSave={logic.handleSaveEditedImage}
          onSend={logic.handleSendEditedImage}
          onDiscard={logic.handleDiscardImage}
          onClose={() => logic.setIsImageEditorOpen(false)}
          onAddFile={logic.handleTriggerFileSelect}
          isDark={isDark}
        />
      )}
    </div>
  );
};

SendMessage.propTypes = {
  inputText: PropTypes.string.isRequired,
  setInputText: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  selectedFile: PropTypes.object,
  setSelectedFile: PropTypes.func.isRequired,
  filePreview: PropTypes.object,
  setFilePreview: PropTypes.func.isRequired,
  isSending: PropTypes.bool.isRequired,
  commands: PropTypes.array,
  quickCommands: PropTypes.array,
  onSelectCommand: PropTypes.func,
  onReloadAtajos: PropTypes.func,
  clientOpenTickets: PropTypes.array,
  onOpenMergeModal: PropTypes.func,
  inputRef: PropTypes.object,
  ticket: PropTypes.object,
  onCreateTicketNote: PropTypes.func,
  onUpdateTicketNote: PropTypes.func,
  isSavingNote: PropTypes.bool,
  replyingTo: PropTypes.object,
  onCancelReply: PropTypes.func,
  onTyping: PropTypes.func,
  onStopTyping: PropTypes.func
};

export default SendMessage;
